import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, useTheme } from '@mui/material'
import { widgetStyles } from '../common/Widget'
import AlertUI from '../../../generic/AlertUI'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import useGraphicData from './hooks/useGraphicData'
import WidgetHeader from './components/WidgetHeader'
import GraphicChart from './components/GraphicChart'
import StatusOverlay from './components/StatusOverlay'

/**
 * GraphicWidget Refactored
 * Displays a line/area chart with historical data for a variable.
 * Implements best practices, modularity, and state management.
 */
const GraphicWidget = ({
  data,
  title = '',
  format = '',
  min = 0,
  max = 60,
  variableName,
  intervalMinutes = 30
}) => {
  const { t_ } = useSmartTranslation()
  // Unified configuration
  const config = {
    variableName: data?.variableName || variableName,
    title: data?.title || title,
    min: data?.min !== undefined ? Number(data.min) : min,
    max: data?.max !== undefined ? Number(data.max) : max,
    intervalMinutes: data?.intervalMinutes !== undefined ? Number(data.intervalMinutes) : intervalMinutes,
    format,
    timeRangeMode: data?.timeRangeMode,
    time: data?.time,
    startTime: data?.startTime,
    endTime: data?.endTime
  }

  const theme = useTheme()
  const styles = widgetStyles(theme)
  const [AlertElement, showAlert] = AlertUI()

  //  Data fetching hook
  const { dataPoints, loading, error } = useGraphicData({
    variableName: config.variableName,
    format: config.format,
    intervalMinutes: config.intervalMinutes,
    timeRangeMode: config.timeRangeMode,
    time: config.time,
    startTime: config.startTime,
    endTime: config.endTime
  })

  // Error handling using AlertUI
  useEffect(() => {
    if (error) {
      showAlert({ type: 'error', content: t_('Error loading graphic data') })
    }
  }, [error, showAlert, t_])

  const isEmpty = !loading && !error && dataPoints.length === 0

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflow: 'hidden'
      }}
    >
      {AlertElement}

      <WidgetHeader
        title={config.title}
        format={config.format}
        styles={styles}
        t_={t_}
      />

      <Box sx={{ flex: 1, position: 'relative', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <StatusOverlay
          loading={loading}
          error={error}
          empty={isEmpty}
          variableName={config.variableName}
          t_={t_}
        />

        <GraphicChart
          dataPoints={dataPoints}
          min={config.min}
          max={config.max}
          format={config.format}
          t_={t_}
          variableName={config.variableName}
        />
      </Box>
    </Box>
  )
}

GraphicWidget.propTypes = {
  data: PropTypes.shape({
    variableName: PropTypes.string,
    title: PropTypes.string,
    min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    intervalMinutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    time: PropTypes.string,
    startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    timeRangeMode: PropTypes.string
  }),
  title: PropTypes.string,
  format: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  variableName: PropTypes.string,
  intervalMinutes: PropTypes.number
}

export default GraphicWidget
